import { hasReadableData } from "./document-intake-state";

export const DOCUMENT_TYPES = {
    IDENTIFICATION: "identificacion_asegurado",
    AUTHORIZATION_LETTER: "carta_autorizacion",
    ACCIDENT_NOTICE: "aviso_accidente",
    POLICY_PROOF: "comprobante_poliza",
    PASSPORT: "pasaporte",
    TUTOR_IDENTIFICATION: "identificacion_tutor",
};

export const DOCUMENT_LABELS = {
    [DOCUMENT_TYPES.IDENTIFICATION]: "Identificación del asegurado",
    [DOCUMENT_TYPES.AUTHORIZATION_LETTER]: "Carta de autorización",
    [DOCUMENT_TYPES.ACCIDENT_NOTICE]: "Aviso de accidente",
    [DOCUMENT_TYPES.POLICY_PROOF]: "Comprobante de seguro o póliza",
    [DOCUMENT_TYPES.PASSPORT]: "Pasaporte",
    [DOCUMENT_TYPES.TUTOR_IDENTIFICATION]: "Identificación del padre o tutor",
};

const { IDENTIFICATION, AUTHORIZATION_LETTER, ACCIDENT_NOTICE, POLICY_PROOF, PASSPORT, TUTOR_IDENTIFICATION } = DOCUMENT_TYPES;

const get = (object, path) =>
    path.split(".").reduce((node, key) => (node === null || node === undefined ? undefined : node[key]), object);

const clean = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).replace(/\s+/g, " ").trim();
};

const isValidDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
    }
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

/** Documentos de los que se toman los datos del asegurado, del mas confiable al menos. */
const IDENTITY_SOURCES = [
    [IDENTIFICATION, { name: "datos.nombres", flastname: "datos.apellido_paterno", mlastname: "datos.apellido_materno", birthdate: "datos.fecha_nacimiento" }],
    [PASSPORT, { name: "datos.nombres", flastname: "datos.apellidos", birthdate: "datos.fecha_nacimiento" }],
    [ACCIDENT_NOTICE, { name: "datos.asegurado_afectado.nombres", flastname: "datos.asegurado_afectado.apellido_paterno", mlastname: "datos.asegurado_afectado.apellido_materno", birthdate: "datos.asegurado_afectado.fecha_nacimiento" }],
    [AUTHORIZATION_LETTER, { birthdate: "datos_secundarios.fecha_nacimiento_paciente" }],
    [POLICY_PROOF, { name: "datos.asegurado_titular.nombres", flastname: "datos.asegurado_titular.apellido_paterno", mlastname: "datos.asegurado_titular.apellido_materno" }],
];

const POLICY_SOURCES = [
    [POLICY_PROOF, "datos.numero_poliza"],
    [ACCIDENT_NOTICE, "datos.poliza.numero_poliza"],
    [AUTHORIZATION_LETTER, "datos_secundarios.numero_poliza"],
];

/** Campo del caso -> de donde se lee, en orden de preferencia. */
const SIMPLE_SOURCES = {
    atentionIden: [[AUTHORIZATION_LETTER, "datos.atencion"]],
    identificator: [[AUTHORIZATION_LETTER, "datos.folio"]],
    diagnosis: [[AUTHORIZATION_LETTER, "datos_secundarios.diagnostico"], [ACCIDENT_NOTICE, "datos_secundarios.diagnostico"]],
    treatmentDoctorName: [[AUTHORIZATION_LETTER, "datos_secundarios.medico_tratante"], [ACCIDENT_NOTICE, "datos_secundarios.medico_tratante"]],
};

const normalizePolicy = (value) => clean(value).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

/**
 * Analisis utilizables: leidos por el modelo y que no resultaron ser otro documento. Un
 * documento equivocado (documento_reconocido === false) nunca aporta datos al caso.
 */
const usable = (analyses) =>
    (analyses || []).filter(
        (analysis) => hasReadableData(analysis) && analysis.result && analysis.result.documento_reconocido !== false
    );

const firstOf = (analyses, type) => usable(analyses).find((analysis) => analysis.documentType === type);

/**
 * Datos del caso que se pueden prellenar con lo que leyo la IA.
 *
 * Devuelve { values, sources }: `values` son campos del DTO del caso (solo los que se
 * encontraron) y `sources` dice de que documento salio cada uno. El nombre del asegurado sale
 * completo de un solo documento, para no mezclar apellidos de personas distintas.
 */
export const buildPrefill = (analyses) => {
    const values = {};
    const sources = {};
    const put = (field, value, type) => {
        if (clean(value) !== "") {
            values[field] = clean(value);
            sources[field] = type;
        }
    };

    for (const [type, paths] of IDENTITY_SOURCES) {
        const analysis = firstOf(analyses, type);
        if (!analysis) {
            continue;
        }
        const name = paths.name ? clean(get(analysis.result, paths.name)) : "";
        const flastname = paths.flastname ? clean(get(analysis.result, paths.flastname)) : "";
        const mlastname = paths.mlastname ? clean(get(analysis.result, paths.mlastname)) : "";
        if (name || flastname || mlastname) {
            put("assuredName", name, type);
            put("assuredFlastname", flastname, type);
            put("assuredMlastname", mlastname, type);
            break;
        }
    }

    for (const [type, paths] of IDENTITY_SOURCES) {
        const analysis = firstOf(analyses, type);
        const birthdate = analysis && paths.birthdate ? clean(get(analysis.result, paths.birthdate)) : "";
        if (isValidDate(birthdate)) {
            put("assuredBrthdate", birthdate, type);
            break;
        }
    }

    for (const [type, path] of POLICY_SOURCES) {
        const analysis = firstOf(analyses, type);
        const policy = analysis ? normalizePolicy(get(analysis.result, path)) : "";
        if (policy) {
            put("policy", policy, type);
            break;
        }
    }

    Object.entries(SIMPLE_SOURCES).forEach(([field, candidates]) => {
        for (const [type, path] of candidates) {
            const analysis = firstOf(analyses, type);
            const value = analysis ? clean(get(analysis.result, path)) : "";
            if (value) {
                put(field, value, type);
                break;
            }
        }
    });

    return { values, sources };
};

const comparable = (value) =>
    clean(value)
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z ]/g, "")
        .toUpperCase()
        .trim();

/**
 * Avisos de congruencia entre documentos (informativos: nunca bloquean). Compara el nombre
 * del asegurado contra la identificacion y la poliza entre los documentos que la traen.
 */
export const getCoherenceWarnings = (analyses) => {
    const warnings = [];
    const identification = firstOf(analyses, IDENTIFICATION);
    const passport = firstOf(analyses, PASSPORT);
    const identity = identification || passport;

    if (identity) {
        const reference = {
            name: comparable(get(identity.result, "datos.nombres")),
            flastname: comparable(get(identity.result, identification ? "datos.apellido_paterno" : "datos.apellidos")),
        };
        [ACCIDENT_NOTICE, POLICY_PROOF].forEach((type) => {
            const analysis = firstOf(analyses, type);
            if (!analysis) {
                return;
            }
            const prefix = type === ACCIDENT_NOTICE ? "datos.asegurado_afectado" : "datos.asegurado_titular";
            const flastname = comparable(get(analysis.result, `${prefix}.apellido_paterno`));
            const name = comparable(get(analysis.result, `${prefix}.nombres`));
            if (reference.flastname && flastname && reference.flastname !== flastname) {
                warnings.push(`El apellido paterno del documento "${DOCUMENT_LABELS[type]}" no coincide con el de la identificación.`);
            } else if (reference.name && name && reference.name !== name) {
                warnings.push(`El nombre del documento "${DOCUMENT_LABELS[type]}" no coincide con el de la identificación.`);
            }
        });

        const letter = firstOf(analyses, AUTHORIZATION_LETTER);
        const patient = letter ? comparable(get(letter.result, "datos_secundarios.nombre_paciente")) : "";
        if (patient && reference.flastname && !patient.includes(reference.flastname)) {
            warnings.push("El paciente de la carta de autorización no coincide con el de la identificación.");
        }
    }

    const policies = POLICY_SOURCES.map(([type, path]) => {
        const analysis = firstOf(analyses, type);
        return analysis ? { type, policy: normalizePolicy(get(analysis.result, path)) } : null;
    }).filter((entry) => entry && entry.policy);
    if (policies.some((entry) => entry.policy !== policies[0].policy)) {
        warnings.push("El número de póliza no es el mismo en todos los documentos.");
    }

    return warnings;
};

/** Fecha de nacimiento (AAAA-MM-DD) que se pudo leer de los documentos, o "" si ninguno la trae. */
export const getBirthdate = (analyses) => buildPrefill(analyses).values.assuredBrthdate || "";

/**
 * Igual que buildPrefill, pero respeta la fecha de nacimiento que la persona capturo a mano en
 * el asistente cuando ningun documento la trajo. Una fecha capturada a mano no lleva halo verde:
 * no la leyo la IA.
 */
export const buildPrefillWithContext = (analyses, context = {}) => {
    const prefill = buildPrefill(analyses);
    if (context.manualBirthdate && isValidDate(context.manualBirthdate)) {
        prefill.values.assuredBrthdate = context.manualBirthdate;
        delete prefill.sources.assuredBrthdate;
    }
    return prefill;
};

/** Menor de 18 anos a hoy. Una fecha ausente o invalida no es menor: no se inventa el aviso. */
export const isMinorByBirthdate = (birthdate, today = new Date()) => {
    const value = clean(birthdate);
    if (!isValidDate(value)) {
        return false;
    }
    const [year, month, day] = value.split("-").map(Number);
    let age = today.getFullYear() - year;
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
        age -= 1;
    }
    return age >= 0 && age < 18;
};

/** Nombre del padre o tutor tal como lo leyo la IA de su identificacion ("NOMBRES APELLIDOS"). */
export const getTutorName = (analyses) => {
    const analysis = firstOf(analyses, TUTOR_IDENTIFICATION);
    if (!analysis) {
        return "";
    }
    return ["datos.nombres", "datos.apellido_paterno", "datos.apellido_materno"]
        .map((path) => clean(get(analysis.result, path)))
        .filter(Boolean)
        .join(" ");
};

/**
 * Avisos informativos del ingreso (nunca bloquean): congruencia entre documentos, pasaporte
 * pendiente de validacion manual y menor de edad sin identificacion del tutor. `birthdate`
 * permite usar la fecha capturada en el formulario en lugar de la que leyo la IA.
 */
export const getIntakeNotices = (analyses, { birthdate } = {}) => {
    const notices = [...getCoherenceWarnings(analyses)];

    if (firstOf(analyses, PASSPORT)) {
        notices.push("El pasaporte requiere validación manual.");
    }

    const birth = clean(birthdate) || buildPrefill(analyses).values.assuredBrthdate;
    if (isMinorByBirthdate(birth) && !getLoadedTypes(analyses).includes(TUTOR_IDENTIFICATION)) {
        notices.push("El paciente es menor de edad: adjunta la identificación del padre o tutor.");
    }

    return notices;
};

/**
 * Documentos que ya cuentan como cargados: existe el analisis y no resulto ser otro documento.
 * Un analisis que fallo (el archivo quedo guardado pero no se pudo leer) cuenta como cargado:
 * lo revisara una persona.
 */
export const getLoadedTypes = (analyses) =>
    (analyses || [])
        .filter((analysis) => !(hasReadableData(analysis) && analysis.result && analysis.result.documento_reconocido === false))
        .map((analysis) => analysis.documentType);

/**
 * Lista de documentos del expediente con su estado. Cada requisito es un tipo o una lista de
 * alternativas (la identificacion se cumple con INE o con pasaporte). Devuelve
 * [{ types, label, loaded }].
 */
export const getDocumentChecklist = (required, analyses) => {
    const loaded = getLoadedTypes(analyses);
    return required.map((requirement) => {
        const types = Array.isArray(requirement) ? requirement : [requirement];
        return {
            types,
            label: types.map((type) => DOCUMENT_LABELS[type]).join(" o "),
            loaded: types.some((type) => loaded.includes(type)),
        };
    });
};

/** Los requisitos del expediente que todavia no se cumplen. */
export const getMissingDocuments = (required, analyses) =>
    getDocumentChecklist(required, analyses).filter((document) => !document.loaded);

/** Estado de un campo prellenado, para pintar su halo: verde (la IA lo capturo) o naranja (falta). */
export const getHaloClass = ({ filled, fromAI }) => {
    if (!filled) {
        return "di-halo-missing";
    }
    return fromAI ? "di-halo-ok" : "";
};
